import crypto from 'crypto';

import { APP_VERSION, IS_STAGING } from '@/env';
import { SALT } from '@/utils/constants';
import { AppRefQueryParam, getClientHeaderFromAppRef } from '@/utils/http';

export interface CreateAccountValues {
  confirmPassword?: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  sub?: string;
  token?: {
    type: 'Apple' | 'Facebook' | 'Google';
    value: string;
  };
}

export type RequestBody = {
  agree: boolean;
  apple_id_token?: string;
  email: string;
  email_opt_in: boolean;
  facebook_access_token?: string;
  first_name: string;
  google_id_token?: string;
  language_tag: string;
  last_name: string;
  password: string;
  token: string;
  verified?: boolean;
};

interface Props {
  appRef: AppRefQueryParam | string | undefined;
  isEmailVerified?: boolean;
  languageTag: string;
  optIn?: boolean;
  token?: {
    type: 'Apple' | 'Facebook' | 'Google';
    value: string;
  };
  values: CreateAccountValues;
}
export async function CreateAccount({
  appRef,
  isEmailVerified,
  optIn,
  token,
  values,
  languageTag,
}: Props): Promise<Response> {
  const apiUrl = `https://webproxy${IS_STAGING ? '-staging' : ''}.bible.com/users/create.json`;

  const hash = crypto.createHash('md5').update(`${values.email}${SALT}${values.password}`).digest('hex');

  const requestBody: RequestBody = {
    agree: true,
    email: values.email,
    email_opt_in: optIn ?? false,
    first_name: values.firstName,
    language_tag: languageTag,
    last_name: values.lastName,
    password: values?.password ?? '',
    token: token?.type ? '' : hash,
    verified: isEmailVerified,
  };

  if (token) {
    switch (token.type) {
      case 'Google':
        requestBody.google_id_token = token.value;
        break;
      case 'Apple':
        requestBody.apple_id_token = token.value;
        break;
      case 'Facebook':
        requestBody.facebook_access_token = token.value;
        break;
      default:
        throw new Error('Unsupported token type');
    }
  }

  const requestOptions = {
    body: JSON.stringify(requestBody),
    method: 'POST',
  };

  const headers: Record<string, string> = {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'X-YouVersion-App-Platform': 'web',
    'X-YouVersion-App-Version': APP_VERSION,
    'X-YouVersion-Client': getClientHeaderFromAppRef(appRef as AppRefQueryParam) ?? 'youversion',
  };

  try {
    return await fetch(apiUrl, { ...requestOptions, headers });
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error('An unexpected error occurred'));
  }
}
